import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api-error.ts';

const SECOND = 1000;

export const staleTimes = {
  reference: 30 * 60 * SECOND,
  catalog: 2 * 60 * SECOND,
  inventory: 10 * SECOND,
  orders: 15 * SECOND,
  dashboard: 30 * SECOND,
  settings: 5 * 60 * SECOND,
} as const;

export type ResourceKind = keyof typeof staleTimes;

const MAX_RETRIES = 2;

export const shouldRetry = (failureCount: number, error: unknown): boolean =>
  failureCount < MAX_RETRIES && (!(error instanceof ApiError) || error.isRetryable);

export type QueryClientOverrides = { retry?: boolean | ((failureCount: number, error: unknown) => boolean) };

export const createQueryClient = (overrides: QueryClientOverrides = {}): QueryClient =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: staleTimes.orders,
        retry: overrides.retry ?? shouldRetry,
        refetchOnWindowFocus: true,
        placeholderData: (previous: unknown) => previous,
      },
      mutations: { retry: 0 },
    },
  });

type Params = Record<string, unknown>;

export const queryKeys = {
  dashboard: (tenantId: string) => ['dashboard', tenantId] as const,
  performance: (tenantId: string, params: Params) => ['dashboard', tenantId, 'performance', params] as const,
  stockOverview: (tenantId: string) => ['dashboard', tenantId, 'stock'] as const,
  tenant: (tenantId: string) => ['tenant', tenantId] as const,
  branding: (tenantId: string) => ['branding', tenantId] as const,
  orders: (tenantId: string, params: Params) => ['orders', tenantId, params] as const,
  products: (tenantId: string, params: Params) => ['products', tenantId, params] as const,
  product: (tenantId: string, id: string) => ['products', tenantId, 'detail', id] as const,
  catalogCode: (tenantId: string, code: string) => ['products', tenantId, 'code', code] as const,
  nextReference: (tenantId: string, prefix: string) =>
    ['products', tenantId, 'next-reference', prefix] as const,
  productStatistics: (tenantId: string, productId: string, params: Params) =>
    ['products', tenantId, 'statistics', productId, params] as const,
  variantNames: (tenantId: string, params: Params) =>
    ['products', tenantId, 'variant-names', params] as const,
  categories: (tenantId: string, params: Params) => ['categories', tenantId, params] as const,
  options: (tenantId: string) => ['options', tenantId] as const,
  locations: (tenantId: string, params: Params) => ['locations', tenantId, params] as const,
  stockLevels: (tenantId: string, params: Params) => ['stock-levels', tenantId, params] as const,
  lowStock: (tenantId: string, params: Params) => ['low-stock', tenantId, params] as const,
  movements: (tenantId: string, params: Params) => ['movements', tenantId, params] as const,
  customers: (tenantId: string, params: Params) => ['customers', tenantId, params] as const,
  customer: (tenantId: string, id: string) => ['customers', tenantId, 'detail', id] as const,
  order: (tenantId: string, id: string) => ['orders', tenantId, 'detail', id] as const,
  geoAreas: (tenantId: string) => ['geo-areas', tenantId] as const,
  carriers: (tenantId: string) => ['carriers', tenantId] as const,
  sender: (tenantId: string) => ['sender', tenantId] as const,
  handovers: (tenantId: string) => ['handovers', tenantId] as const,
  handover: (tenantId: string, id: string) => ['handovers', tenantId, 'detail', id] as const,
  readyParcels: (tenantId: string, id: string, search: string) =>
    ['handovers', tenantId, 'ready', id, search] as const,
  remittances: (tenantId: string, params: Params) => ['remittances', tenantId, params] as const,
  remittance: (tenantId: string, id: string) => ['remittances', tenantId, 'detail', id] as const,
  settlements: (tenantId: string, params: Params) => ['settlements', tenantId, params] as const,
  financeOverview: (tenantId: string, params: Params) => ['finance-overview', tenantId, params] as const,
  statistics: (tenantId: string, params: Params) => ['statistics', tenantId, params] as const,
  expenses: (tenantId: string, params: Params) => ['expenses', tenantId, params] as const,
  notifications: (tenantId: string, params: Params) => ['notifications', tenantId, params] as const,
  subsidiaries: (tenantId: string) => ['subsidiaries', tenantId] as const,
  team: (tenantId: string) => ['team', tenantId] as const,
  history: (tenantId: string, params: Params) => ['history', tenantId, params] as const,
  tenants: () => ['tenants'] as const,
} as const;
